import { parseCustomReportConfiguration } from "#batteries-included-components/Layouts/CustomReport/CustomReportConfigurationPanel/CustomReportConfigurationPanel.helpers";
import { CustomReportDetailLayout } from "#batteries-included-components/Layouts/CustomReport/CustomReportDetailLayout";
import {
  getCustomReportFilters,
  getCustomReportGroupBy,
  type CustomReportVariantType,
} from "#batteries-included-components/Layouts/CustomReport/CustomReportDetailLayout.helpers";
import { CreateCustomReportModal } from "#batteries-included-components/Modals/CustomReports/CreateCustomReportModal";
import { useNavigate, useParams } from "#routers/hooks";
import { CustomReportsDetailRoutePath } from "#routes/reports/custom-reports/[reportVariant]/[reportId]/detail";
import { CustomReportCreateRoutePath } from "#routes/reports/custom-reports/[reportVariant]/create/index";
import { useStorageKey } from "#src/hooks/useStorageKey";
import { useBreadcrumbsFromRoute } from "#src/utils/route";
import { Button, Page, useFilters } from "@validereinc/common-components";
import { getFilterObject } from "@validereinc/domain";
import { toTitleCaseString } from "@validereinc/utilities";
import React, { useEffect, useState } from "react";

export const CreateCustomReportPage = () => {
  const navigate = useNavigate();
  const [breadcrumbs] = useBreadcrumbsFromRoute(CustomReportCreateRoutePath);
  const { reportVariant = "records" } =
    useParams<
      Exclude<typeof CustomReportCreateRoutePath.pathParams, undefined>
    >();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [filterConfigStorageKey, tableConfigStorageKey, tableStateStorageKey] =
    useStorageKey(
      ["filter-configuration", "table-configuration", "table-state"],
      "custom-report-detail"
    );
  const [configStorageKey] = useStorageKey(
    ["custom-report-config"],
    "custom-report-detail"
  );

  const [filters] = useFilters(filterConfigStorageKey);
  const [configuration, setConfiguration] = useFilters(configStorageKey);

  const { rows, assetType } = parseCustomReportConfiguration(configuration);

  useEffect(() => {
    setConfiguration({});
  }, []);

  const filter = getFilterObject(
    getCustomReportFilters(
      reportVariant as CustomReportVariantType,
      assetType,
      configuration,
      filters
    )
  );

  const reportPrefixTxt = toTitleCaseString(reportVariant, { intent: "ui" });

  return (
    <>
      <Page
        category={`${reportPrefixTxt} Custom Report`}
        title={`Unsaved ${reportPrefixTxt} Report`}
        actionRow={[
          <Button
            key="save-custom-report"
            variant="primary"
            onClick={() => setIsCreateModalOpen(true)}
          >
            Save Report
          </Button>,
        ]}
        breadcrumbs={breadcrumbs}
      >
        <CustomReportDetailLayout
          configStorageKey={configStorageKey}
          reportVariant={reportVariant as CustomReportVariantType}
          filterConfigStorageKey={filterConfigStorageKey}
          tableConfigStorageKey={tableConfigStorageKey}
          tableStateStorageKey={tableStateStorageKey}
        />
      </Page>
      <CreateCustomReportModal
        variant={reportVariant as CustomReportVariantType}
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
        }}
        onSave={(data) => {
          navigate({
            ...CustomReportsDetailRoutePath.toLinkParts({
              pathParams: { reportId: data.id, reportVariant: reportVariant },
            }),
            state: data,
          });
        }}
        filter={filter}
        groupBy={getCustomReportGroupBy(
          reportVariant as CustomReportVariantType,
          assetType,
          rows
        )}
      />
    </>
  );
};
